'use client'

import type { Didomi } from '@/util/analytics'

import type { FreestarSuppressionReason } from './types'

export type FreestarConsentResult =
    | { status: 'allowed' }
    | {
          status: 'suppressed'
          reason: Extract<FreestarSuppressionReason, 'consent_suppressed' | 'didomi_unavailable'>
      }

const freestarConsentPromises = new Map<string, Promise<FreestarConsentResult>>()

export const FREESTAR_DIDOMI_VENDOR_ID = '850'

function hasGrantedConsent(didomi: Didomi, vendorId: string) {
    return didomi.getUserConsentStatusForVendor(vendorId) === true
}

export function resolveFreestarConsentFromDidomi(
    didomi: Didomi | undefined,
    vendorId: string = FREESTAR_DIDOMI_VENDOR_ID
): Promise<FreestarConsentResult> {
    if (!didomi) {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    if (didomi.isConsentRequired?.() === false) {
        return Promise.resolve({ status: 'allowed' })
    }

    if (hasGrantedConsent(didomi, vendorId)) {
        return Promise.resolve({ status: 'allowed' })
    }

    const observable = didomi.getObservableOnUserConsentStatusForVendor?.(vendorId)
    if (!observable) {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    return new Promise((resolve) => {
        observable.subscribe((consentStatusForVendor) => {
            if (consentStatusForVendor === undefined) {
                return
            }
            resolve(
                consentStatusForVendor ? { status: 'allowed' } : { status: 'suppressed', reason: 'consent_suppressed' }
            )
        })
    })
}

export function waitForFreestarConsent(vendorId: string = FREESTAR_DIDOMI_VENDOR_ID): Promise<FreestarConsentResult> {
    if (typeof window === 'undefined') {
        return Promise.resolve({ status: 'suppressed', reason: 'didomi_unavailable' })
    }

    const cachedConsent = freestarConsentPromises.get(vendorId)
    if (cachedConsent) {
        return cachedConsent
    }

    const consentPromise = waitForFreestarConsentResult(vendorId)
    freestarConsentPromises.set(vendorId, consentPromise)
    return consentPromise
}

function waitForFreestarConsentResult(vendorId: string): Promise<FreestarConsentResult> {
    if (window.Didomi) {
        return resolveFreestarConsentFromDidomi(window.Didomi, vendorId)
    }

    window.didomiOnReady = window.didomiOnReady || []
    return new Promise((resolve) => {
        window.didomiOnReady.push((didomi) => {
            resolve(resolveFreestarConsentFromDidomi(didomi, vendorId))
        })
    })
}
